// ADC-IMPLEMENTS: <adc-006a-feature-hook-all-01>
import { useState, useEffect } from 'react';
import {
  collectionGroup,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import {
  mapFirestoreToIncident,
  computeIncidentSummary,
  type Incident,
  type IncidentSummary,
} from '@/lib/incidentUtils';

export interface UseAllIncidentsOptions {
  includeClosedIncidents?: boolean;
  limit?: number;
}

export interface UseAllIncidentsReturn {
  incidents: Incident[];
  summary: IncidentSummary;
  loading: boolean;
  error: string | null;
  resolveIncident: (incident: Incident, notes: string) => Promise<void>;
  dismissIncident: (incident: Incident, notes: string) => Promise<void>;
  escalateIncident: (incident: Incident, escalateTo: string) => Promise<void>;
}

const EMPTY_SUMMARY: IncidentSummary = {
  total: 0,
  bySeverity: { critical: 0, high: 0, medium: 0, low: 0 },
  byStatus: { PENDING: 0, ACTIVE: 0, ESCALATED: 0, RESOLVED: 0, FALSE_POSITIVE: 0 },
};

export function useAllIncidents(options: UseAllIncidentsOptions = {}): UseAllIncidentsReturn {
  const { includeClosedIncidents = false, limit: maxResults = 100 } = options;
  const { user } = useAuth();
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [summary, setSummary] = useState<IncidentSummary>(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setIncidents([]);
      setSummary(EMPTY_SUMMARY);
      setLoading(false);
      return;
    }

    const incidentsGroupRef = collectionGroup(db, 'incidents');

    const constraints = [];
    // Filter by owner -- required for collection group queries because
    // Firestore security rules cannot use get() with collectionGroup queries.
    constraints.push(where('user_id', '==', user.uid));
    if (!includeClosedIncidents) {
      constraints.push(where('firebase_status', '==', 'active'));
    }
    constraints.push(orderBy('severity_score', 'desc'));
    constraints.push(orderBy('created_at', 'desc'));

    const incidentsQuery = query(incidentsGroupRef, ...constraints);

    const unsubscribe = onSnapshot(
      incidentsQuery,
      (snapshot) => {
        const mapped = snapshot.docs
          .map(d => mapFirestoreToIncident(d.id, d.data()))
          .slice(0, maxResults);

        setIncidents(mapped);
        setSummary(computeIncidentSummary(mapped));
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error in all-incidents listener:', err);
        setError(`Failed to listen for incidents: ${err.message}`);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, includeClosedIncidents, maxResults]);

  const getIncidentRef = (incident: Incident) =>
    doc(db, `connections/${incident.connection_id}/conversations/${incident.conversation_id}/incidents/${incident.id}`);

  async function resolveIncident(incident: Incident, notes: string): Promise<void> {
    await updateDoc(getIncidentRef(incident), {
      status: 'RESOLVED',
      firebase_status: 'closed',
      closed_at: serverTimestamp(),
      updated_at: serverTimestamp(),
      resolution_notes: notes,
    });
  }

  async function dismissIncident(incident: Incident, notes: string): Promise<void> {
    await updateDoc(getIncidentRef(incident), {
      status: 'FALSE_POSITIVE',
      firebase_status: 'closed',
      closed_at: serverTimestamp(),
      updated_at: serverTimestamp(),
      resolution_notes: notes,
    });
  }

  async function escalateIncident(incident: Incident, escalateTo: string): Promise<void> {
    await updateDoc(getIncidentRef(incident), {
      status: 'ESCALATED',
      escalated_to: escalateTo,
      updated_at: serverTimestamp(),
    });
  }

  return { incidents, summary, loading, error, resolveIncident, dismissIncident, escalateIncident };
}
